import { useState, useEffect, useCallback } from "react"
import { initPayrails, tokenizeCard } from "@/features/payments"
import { paymentService } from "@/features/payments"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { usePurchaseStore } from "@/stores/purchase-store"
import { showErrorToast } from "@/stores/toast-store"
import type { ProductPlan } from "@/features/products/types"
import { paymentErrorMessage } from "../domain/payment-error"
import type { PayrailsFieldKey, PayrailsFieldState } from "../providers/payrails"
import type { PaymentStatus, PayrailsTheme } from "../types"

/* eslint-disable @typescript-eslint/no-explicit-any */

function detectApplePay(): boolean {
  const APS = (window as any).ApplePaySession
  return Boolean(window.isSecureContext && APS?.canMakePayments?.())
}

/** Whether Apple Pay can be offered on this device/context. */
export function isApplePayAvailable(): boolean {
  return detectApplePay()
}

interface UseCheckoutOptions {
  /** Payrails card-form theme. */
  theme?: PayrailsTheme
  /** Mount the Payrails card form. Disable for Apple-Pay-only flows. */
  initCard?: boolean
}

export function useCheckout(
  plan: ProductPlan | undefined,
  email: string,
  options: UseCheckoutOptions = {},
) {
  const { theme = "default", initCard = true } = options
  const [sdkReady, setSdkReady] = useState(false)
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [applePayAvailable] = useState(detectApplePay)
  const [status, setStatus] = useState<PaymentStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  // Per-field focus/validity, so the card sheet can draw each field's border in our
  // DOM (the secure iframes are borderless — see PayrailsFieldState).
  const [fieldStates, setFieldStates] = useState<Partial<Record<PayrailsFieldKey, PayrailsFieldState>>>(
    {},
  )
  const setPurchaseSuccess = usePurchaseStore((s) => s.setSuccess)

  useEffect(() => {
    if (!initCard) return
    let cancelled = false
    let fallback: ReturnType<typeof setTimeout> | null = null
    // Reveal the form only once every hosted field has actually rendered (all three
    // Payrails READY events), not when init() merely resolves — otherwise the covers
    // lift before the iframes paint and their white default background flashes in.
    const reveal = () => {
      if (cancelled) return
      if (fallback) {
        clearTimeout(fallback)
        fallback = null
      }
      setSdkReady(true)
    }
    initPayrails(theme, reveal, (key, state) => {
      if (!cancelled) setFieldStates((prev) => ({ ...prev, [key]: state }))
    })
      .then(() => {
        // Safety net: reveal anyway if `onReady` never arrives (older SDK / hiccup).
        if (!cancelled) fallback = setTimeout(reveal, 6000)
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[Payrails] init error:", err)
          setSdkError("Payment form failed to load. Please refresh.")
        }
      })
    return () => {
      cancelled = true
      if (fallback) clearTimeout(fallback)
    }
  }, [theme, initCard])

  const payWithCard = useCallback(
    async (billing?: { firstName?: string; lastName?: string }): Promise<boolean> => {
      // Early guards surface via the toast (the sheet no longer renders an inline
      // error line): a silent `return false` here reads as "the button does nothing".
      if (status === "processing") return false
      if (!plan) {
        showErrorToast("Payment error", "Please try again.")
        return false
      }
      if (!sdkReady) {
        showErrorToast("Payment error", "Payment form is still loading.")
        return false
      }
      if (!email) {
        showErrorToast("Payment error", "Please enter your email to continue.")
        return false
      }

      setStatus("processing")
      setError(null)
      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        productId: plan.id,
        price: plan.price,
        currency: plan.currency,
        paymentMethod: "card",
      })

      try {
        const instrument = await tokenizeCard()
        const sub = await paymentService.createCardSubscription({
          planId: plan.priceId || plan.id,
          email,
          instrument,
          firstName: billing?.firstName,
          lastName: billing?.lastName,
        })
        setStatus("success")
        setPurchaseSuccess(sub.id, plan.id)
        trackEvent(ANALYTICS_EVENTS.PURCHASE, {
          productId: plan.id,
          transactionId: sub.id,
          price: plan.price,
          currency: plan.currency,
          paymentMethod: "card",
        })
        return true
      } catch (err) {
        const msg = paymentErrorMessage(err)
        setStatus("error")
        setError(msg)
        showErrorToast("Payment error", msg)
        trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, { productId: plan.id, metadata: { error: msg } })
        return false
      }
    },
    [status, sdkReady, email, plan, setPurchaseSuccess],
  )

  const payWithApplePay = useCallback(async (): Promise<boolean> => {
    if (status === "processing") return false
    if (!plan) {
      showErrorToast("Payment error", "Please try again.")
      return false
    }
    const APS = (window as any).ApplePaySession
    if (!window.isSecureContext || !APS?.canMakePayments?.()) {
      showErrorToast("Payment error", "Apple Pay is not available on this device.")
      return false
    }

    const appName = import.meta.env.VITE_APP_NAME || "App"
    // No `recurringPaymentRequest`: Apple issues a device token (DPAN) and the
    // sheet shows a single one-time charge. Recurring billing is handled server-
    // side off the `plan_id` (Payrails vaults the token), not via this request.
    const request = {
      countryCode: "US",
      currencyCode: plan.currency,
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      merchantCapabilities: ["supports3DS"],
      // Email is already collected in the quiz and sent to the provider below,
      // so we don't require it from the Apple Pay sheet (avoids a duplicate prompt).
      requiredBillingContactFields: ["name", "phone", "postalAddress"],
      total: { label: appName, amount: plan.price.toFixed(2) },
    }

    const session = new APS(3, request)

    setStatus("processing")
    setError(null)
    trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
      productId: plan.id,
      price: plan.price,
      currency: plan.currency,
      paymentMethod: "apple_pay",
    })

    return new Promise<boolean>((resolve) => {
      session.onvalidatemerchant = async (event: any) => {
        try {
          const merchantSession = await paymentService.validateApplePayMerchant(
            event.validationURL,
            window.location.hostname,
          )
          session.completeMerchantValidation(merchantSession)
        } catch {
          session.abort()
          setStatus("error")
          setError("Apple Pay verification failed. Try card instead.")
          showErrorToast("Payment error", "Apple Pay verification failed. Try card instead.")
          resolve(false)
        }
      }

      session.onpaymentauthorized = async (event: any) => {
        try {
          const payment = event.payment
          const sub = await paymentService.createApplePaySubscription({
            planId: plan.priceId || plan.id,
            email,
            applePayToken: payment.token.paymentData,
            displayName: payment.token.paymentMethod?.displayName,
            billing: payment.billingContact,
          })
          session.completePayment(APS.STATUS_SUCCESS ?? 0)
          setStatus("success")
          setPurchaseSuccess(sub.id, plan.id)
          trackEvent(ANALYTICS_EVENTS.PURCHASE, {
            productId: plan.id,
            transactionId: sub.id,
            price: plan.price,
            currency: plan.currency,
            paymentMethod: "apple_pay",
          })
          resolve(true)
        } catch (err) {
          session.completePayment(APS.STATUS_FAILURE ?? 1)
          const msg = paymentErrorMessage(err, "Apple Pay failed.")
          setStatus("error")
          setError(msg)
          showErrorToast("Payment error", msg)
          trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, { productId: plan.id, metadata: { error: msg } })
          resolve(false)
        }
      }

      session.oncancel = () => {
        setStatus("idle")
        resolve(false)
      }

      session.begin()
    })
  }, [status, email, plan, setPurchaseSuccess])

  return {
    sdkReady,
    sdkError,
    applePayAvailable,
    status,
    error,
    fieldStates,
    payWithCard,
    payWithApplePay,
  }
}
